import { PageContainer } from "@ant-design/pro-components";
import type { TablePaginationConfig } from "antd";
import { Space } from "antd";
import type { CSSProperties, ReactNode } from "react";
import * as React from "react";
import type { AdminPageActionItem } from "./admin-action-types";
import { AdminLegendGroups } from "./admin-legend-components";
import type { AdminLegendGroup } from "./admin-legend-types";
import { AdminPageNotice, type AdminPageNoticeItem } from "./admin-page";
import { AdminListToolbarActions, AdminPageActions } from "./admin-page-actions";
import type { SharedAdminPageContainerProps } from "./admin-page-container";
import { ADMIN_LIST_PAGINATION_STANDARD, AdminListScaffold } from "./admin-table";

type AdminListWorkspaceTableProps = {
  headerTitle?: ReactNode;
  columns?: unknown;
  tableLayout?: "auto" | "fixed";
  params?: Record<string, unknown>;
  pagination?: false | TablePaginationConfig;
  toolBarRender?:
    | ((
        action: unknown,
        rows: {
          selectedRowKeys?: Array<string | number>;
          selectedRows?: unknown[];
        },
      ) => ReactNode[])
    | false;
  cardProps?:
    | ({
        bodyStyle?: CSSProperties;
      } & Record<string, unknown>)
    | false;
  scroll?: {
    x?: string | number | true;
    y?: string | number;
    scrollToFirstRowOnChange?: boolean;
  };
  tableRender?: (
    props: AdminListWorkspaceTableProps,
    defaultDom: React.ReactElement,
    domList: {
      toolbar?: React.ReactElement;
      alert?: React.ReactElement;
      table?: React.ReactElement;
    },
  ) => React.ReactNode;
};

const ADMIN_LIST_WORKSPACE_TABLE_SHELL_STYLE: CSSProperties = {
  minWidth: 0,
  width: "100%",
  overflowX: "auto",
  overflowY: "hidden",
};

const ADMIN_LIST_WORKSPACE_CARD_BODY_STYLE: CSSProperties = {
  paddingBlockStart: "var(--ant-padding, 16px)",
  paddingInline: "var(--ant-paddingLG, 24px)",
};

function readPositivePageValue(value: unknown): number | undefined {
  if (typeof value !== "number" && typeof value !== "string") {
    return undefined;
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? Math.floor(numberValue) : undefined;
}

function resolveAdminListWorkspacePagination(
  tableElement: React.ReactElement<AdminListWorkspaceTableProps>,
): false | TablePaginationConfig {
  if (tableElement.props.pagination !== undefined) {
    return tableElement.props.pagination;
  }

  const current = readPositivePageValue(tableElement.props.params?.page);
  const pageSize = readPositivePageValue(tableElement.props.params?.pageSize);

  return {
    ...ADMIN_LIST_PAGINATION_STANDARD,
    ...(current === undefined ? {} : { current }),
    ...(pageSize === undefined ? {} : { pageSize }),
  };
}

export type AdminListWorkspaceProps = SharedAdminPageContainerProps & {
  notices?: AdminPageNoticeItem[];
  legendGroups?: AdminLegendGroup[];
  headerActions?: AdminPageActionItem[];
  sidebar?: ReactNode;
  sidebarWidth?: number | string;
  contentBeforeTable?: ReactNode;
  table: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  contentSpacing?: number;
  showTableSkeleton?: boolean;
  children?: ReactNode;
};

export function AdminListWorkspace({
  notices = [],
  legendGroups = [],
  headerActions = [],
  sidebar,
  sidebarWidth = 220,
  contentBeforeTable,
  table,
  footerLeft,
  footerRight,
  contentSpacing = 16,
  showTableSkeleton = false,
  children,
  extra,
  ...containerProps
}: AdminListWorkspaceProps) {
  const sidebarLayoutStyle: CSSProperties = {
    display: "grid",
    gap: contentSpacing,
    alignItems: "start",
    gridTemplateColumns: `${typeof sidebarWidth === "number" ? `${sidebarWidth}px` : sidebarWidth} minmax(0, 1fr)`,
  };
  const contentPanelStyle: CSSProperties = {
    minWidth: 0,
  };
  const resolvedExtra = extra;
  const headerActionsNode =
    headerActions.length > 0 ? <AdminPageActions actions={headerActions} /> : undefined;
  const tableElement = React.isValidElement(table)
    ? (table as React.ReactElement<AdminListWorkspaceTableProps>)
    : null;
  const canInjectTableToolbar = Boolean(tableElement && "columns" in (tableElement.props ?? {}));
  const baseTableRender = tableElement?.props.tableRender;
  const baseToolBarRender = tableElement?.props.toolBarRender;
  const sharedToolBarRender =
    headerActions.length > 0
      ? () => [
          <React.Fragment key="table-actions">
            <AdminListToolbarActions actions={headerActions} />
          </React.Fragment>,
        ]
      : undefined;
  const mergedToolBarRender = sharedToolBarRender
    ? (
        action: unknown,
        rows: { selectedRowKeys?: Array<string | number>; selectedRows?: unknown[] },
      ) => {
        const sharedItems = sharedToolBarRender() ?? [];
        const pageItems =
          typeof baseToolBarRender === "function" ? (baseToolBarRender(action, rows) ?? []) : [];
        return [...sharedItems, ...pageItems];
      }
    : baseToolBarRender;
  const injectedTableProps: Partial<AdminListWorkspaceTableProps> = {};
  if (tableElement && canInjectTableToolbar) {
    if (tableElement.props.headerTitle !== undefined) {
      injectedTableProps.headerTitle = tableElement.props.headerTitle;
    }
    if (mergedToolBarRender !== undefined) {
      injectedTableProps.toolBarRender = mergedToolBarRender;
    }
    injectedTableProps.tableLayout = tableElement.props.tableLayout ?? "auto";
    injectedTableProps.pagination = resolveAdminListWorkspacePagination(tableElement);
    injectedTableProps.cardProps =
      tableElement.props.cardProps === false
        ? false
        : {
            ...tableElement.props.cardProps,
            bodyStyle: {
              ...ADMIN_LIST_WORKSPACE_CARD_BODY_STYLE,
              ...(tableElement.props.cardProps?.bodyStyle ?? {}),
            },
          };
    injectedTableProps.scroll = tableElement.props.scroll
      ? { x: "max-content", ...tableElement.props.scroll }
      : { x: "max-content" as const };
    injectedTableProps.tableRender = (props, defaultDom, domList) =>
      baseTableRender ? (
        <div style={ADMIN_LIST_WORKSPACE_TABLE_SHELL_STYLE}>
          {baseTableRender(props, defaultDom, domList)}
        </div>
      ) : (
        <>
          {domList.toolbar}
          {domList.alert}
          <div style={ADMIN_LIST_WORKSPACE_TABLE_SHELL_STYLE}>{domList.table ?? defaultDom}</div>
        </>
      );
  }
  const resolvedTable =
    tableElement && canInjectTableToolbar
      ? React.cloneElement(tableElement, injectedTableProps)
      : table;

  return (
    <PageContainer {...containerProps} extra={resolvedExtra}>
      <Space
        className="admin-list-workspace-stack"
        vertical
        size={contentSpacing}
        style={{ display: "flex" }}
      >
        <AdminPageNotice notices={notices} />
        <AdminLegendGroups groups={legendGroups} />
        {!canInjectTableToolbar && headerActionsNode ? headerActionsNode : null}
        {contentBeforeTable}
        {sidebar ? (
          <div style={sidebarLayoutStyle}>
            <div style={contentPanelStyle}>{sidebar}</div>
            <div style={contentPanelStyle}>
              <AdminListScaffold
                showTableSkeleton={showTableSkeleton}
                table={resolvedTable}
                footerLeft={footerLeft}
                footerRight={footerRight}
              />
            </div>
          </div>
        ) : (
          <AdminListScaffold
            showTableSkeleton={showTableSkeleton}
            table={resolvedTable}
            footerLeft={footerLeft}
            footerRight={footerRight}
          />
        )}
      </Space>
      {children}
    </PageContainer>
  );
}
